/**
 * The console shell: the one frame every screen renders inside.
 *
 * Two pieces of state live in the URL rather than in memory, on purpose:
 *   - `?theme=dark` so a shared link opens the way the sender saw it, and a reload keeps it.
 *   - `?scope=finance` — the acting scope. It now **defaults to the scope the signed-in account
 *     holds**, so signing in as the finance desk opens a finance-shaped console on its own. The
 *     switcher stays because reviewing seven role-shaped consoles by signing in and out seven
 *     times is not reviewing; it is not a way to grant yourself anything, and it never was:
 *     `can()` is UX shaping and the server is the boundary. It goes when the API does.
 *
 * The shell only ever renders for a signed-in admin. The route guard in `router.tsx` redirects
 * before the component exists — the one exception being a 404, which matches no route and so runs
 * no guard, and is handled explicitly below rather than by hoping.
 */
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Outlet, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { AppShell, Breadcrumb, type NavGroup } from '../components/nav';
import { Chip, Kicker } from '../components/primitives';
import { EmptyState } from '../components/feedback';
import { persistTheme } from '../theme/preference';
import { SECTION_CHORD_MS, sectionTarget } from './sectionKeys';
import { forModuleJump, type WindowSearch } from './urlState';
import { PartialWiringNotice, WiringNotice } from '../components/kit';
import { can, SCOPE_OWNS, type Scope } from '../lib/rbac';
import { GROUP_ORDER, MODULES, type AdminModule, type Wiring } from './modules';
import { AccountMenu, CommandPalette, KeyboardOverlay, IdleLock } from './furniture';
import { authState, signOut, subscribeToAuth } from '../lib/auth/session';
import { watchIdle } from '../lib/auth/idle';
import { STEP_UP_EVENT } from '../lib/api/client';
import { useToast } from './toasts';
import { FreezeBanner } from '../screens/OpsStatus';

/**
 * `none` is a real, reachable state: an invited admin starts with no scopes at all. It is in the
 * switcher so the empty console can be seen rather than only described.
 */
export type ActingScope = Scope | 'none';

export interface ShellSearch extends WindowSearch {
  theme: 'light' | 'dark';
  /** Where the guard was sending you when it found you signed out. Set only on `/login`. */
  next?: string;
  /** The invitation token from a staff invite link. Set only on `/invite`. */
  token?: string;
}

/** Which module the current URL is on, or undefined for a route outside the registry (404, login). */
/**
 * The notice a screen carries for its wiring state, above its content — none when it is live.
 *
 * Exported because no screen is on fixtures any more (E37 wired the last one), so the fixture
 * branch is reachable only by handing it a wiring value directly. It stays: the next screen added
 * before its endpoint exists must still say so on screen.
 */
export function ScreenNotice({ wiring }: { wiring: Wiring }) {
  if (wiring.state === 'fixtures') return <WiringNotice slice={wiring.slice} what={wiring.what} />;
  if (wiring.state === 'partial') return <PartialWiringNotice note={wiring.note} />;
  return null;
}

export function activeModule(pathname: string): AdminModule | undefined {
  const exact = MODULES.find((m) => m.path === pathname);
  if (exact) return exact;
  // A detail route (`/money/ledger/$txnId`) matches by its static prefix.
  return MODULES.find((m) => m.path.includes('$') && pathname.startsWith(m.path.split('/$')[0]! + '/'));
}

/** The nav, filtered to what this scope can actually open. Empty groups do not render. */
export function navFor(scope: ActingScope): NavGroup[] {
  if (scope === 'none') return [];
  return GROUP_ORDER.map((label) => ({
    label,
    items: MODULES.filter((m) => m.group === label && !m.hidden && can(scope, m.scopes)).map((m) => ({
      key: m.key,
      label: m.label,
      icon: m.icon,
    })),
  })).filter((g) => g.items.length > 0);
}

export function Shell() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [keysOpen, setKeysOpen] = useState(false);
  /* Both are always present: the root route's `validateSearch` fills and repairs them, so there
     is no defaulting to do here and no half-populated shape to guard against. */
  const { theme } = useSearch({ strict: false }) as ShellSearch;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  /* What the Outlet is drawing, which is not always `pathname`: for a render after a navigation the
     location is already the new one while the Outlet still holds the old match. Gating on the
     location alone mounted the screen being left for that render, and a screen this scope may not
     open fired its reads into 403s (found by e2e/scopes.spec.ts). Both must be admitted. */
  // The root route always matches, so the list is never empty.
  const rendered = useRouterState({ select: (s) => s.matches[s.matches.length - 1]!.pathname });
  const active = activeModule(pathname);
  const drawn = activeModule(rendered);
  /* Subscribed rather than read once: signing in happens on another route, and a shell that read
     the session at mount would still be showing "signed out" after a successful sign-in. */
  const auth = useSyncExternalStore(subscribeToAuth, authState);
  const me = auth.status === 'signed-in' ? auth.session : null;
  /* Derived, never a parameter. It used to live in the URL because there was nothing else to take
     it from; a `?scope=` on a sign-in link now reads as a switch that no longer exists.
     One acting scope: an admin holding two roles acts as the first until the API says how a
     multi-grant session resolves, because `can()` reads one scope and inventing a resolution rule
     here would be inventing a permission. No grants at all is `none` — a real state, an invited
     admin rather than a broken one (backend 07 §5.8). */
  const scope: ActingScope = me?.scopes[0] ?? 'none';
  const nav = useMemo(() => navFor(scope), [scope]);
  const [locked, setLocked] = useState(false);
  const [lockReason, setLockReason] = useState<'idle' | 'step_up'>('idle');

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  /* An error toast is about the screen whose request failed. Carried onto the next screen it
     reads as that screen's fault (found on the live pass: a city's archive refusal sat over the
     puja catalogue). Receipts are left to expire on their own timer. */
  const { dismissErrors } = useToast();
  useEffect(() => dismissErrors(), [pathname, dismissErrors]);

  const endSession = useCallback(() => {
    void signOut();
    navigate({ to: '/login', search: { theme } as ShellSearch });
  }, [navigate, theme]);

  /* Any route away from a session — expiry, revocation, another tab — lands on sign-in. The
     session module is the one that decides a session is over; this only follows it there. */
  useEffect(() => {
    if (auth.status === 'signed-out' && !active?.bare) {
      navigate({ to: '/login', search: { theme } as ShellSearch });
    }
  }, [auth.status, active?.bare, navigate, theme]);

  /* Doc 02 §2.3 — a walked-away-from console in a room full of people stops showing phone
     numbers. Only armed while someone is signed in and unlocked; there is nothing to hide from
     a visitor who is already looking at the sign-in screen. */
  useEffect(() => {
    if (!me || locked) return;
    return watchIdle(() => {
      setLockReason('idle');
      setLocked(true);
    });
  }, [me, locked]);

  /* Sibling-learnings A5 — the four account-takeover actions answer `step_up_required` when the
     session's sign-in is stale. The lock's unlock is the real two-step and mints a new session,
     which is exactly the fresh sign-in the server asked for. */
  useEffect(() => {
    const raise = () => {
      setLockReason('step_up');
      setLocked(true);
    };
    window.addEventListener(STEP_UP_EVENT, raise);
    return () => window.removeEventListener(STEP_UP_EVENT, raise);
  }, []);

  const setSearch = (next: Partial<ShellSearch>) =>
    navigate({ to: pathname, search: (prev: Partial<ShellSearch>) => ({ ...prev, ...next }) });

  const allowed =
    scope !== 'none' && [active, drawn].every((module) => !module || can(scope, module.scopes));

  const go = useCallback(
    (module: AdminModule) => {
      setPaletteOpen(false);
      /* The window and a queue's filters belong to the screen they were picked on; a jump elsewhere
         starts clean, keeping only the shell's own theme. */
      navigate({ to: module.path, search: (prev: Partial<ShellSearch>) => forModuleJump(prev) as never });
    },
    [navigate],
  );

  /* ⌘K anywhere, `?` only when the operator is not typing — a question mark in a reason field is
     a question mark, not a command. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || tag === 'select';
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      } else if (e.key === '?' && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setKeysOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* The palette is not a way around the nav filter: it offers exactly what the nav would. */
  const reachable = useMemo(
    () => (scope === 'none' ? [] : MODULES.filter((m) => !m.hidden && can(scope, m.scopes))),
    [scope],
  );

  /* `g` then a letter jumps to a nav section (doc 03 §8), only among what the nav shows. The
     letter must follow within SECTION_CHORD_MS; anything else in between drops the chord. */
  useEffect(() => {
    let armedAt = 0;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (armedAt && Date.now() - armedAt <= SECTION_CHORD_MS) {
        armedAt = 0;
        const target = sectionTarget(e.key, reachable);
        if (target) {
          e.preventDefault();
          go(target);
        }
      } else if (e.key === 'g') {
        armedAt = Date.now();
      } else {
        armedAt = 0;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reachable, go]);

  /* Sign-in renders alone: nav, top bar and page header would all be furniture for a console the
     visitor has not entered yet. Placed after every hook, so the hook order never changes. */
  if (active?.bare) return <Outlet />;

  /* Nothing to frame yet. Two ways to get here, both momentary and neither an error: the
     bootstrap has not answered (`unknown`), or the session just ended and the effect above is
     already navigating to sign-in. Rendering a console shell around an empty identity — or a
     "you are signed out" screen nobody will finish reading — is worse than rendering nothing. */
  if (!me) return null;

  return (
    <AppShell
      nav={nav}
      active={active?.key}
      onNavigate={(key) => {
        const target = MODULES.find((m) => m.key === key);
        /* v8 ignore next -- every nav key comes from MODULES; the guard is for a future caller. */
        if (target) go(target);
      }}
      collapsed={collapsed}
      onToggleCollapsed={() => setCollapsed((c) => !c)}
      onSearch={() => setPaletteOpen(true)}
      theme={theme}
      onThemeToggle={() => {
        const next = theme === 'dark' ? 'light' : 'dark';
        persistTheme(next);
        setSearch({ theme: next });
      }}
      user={{ email: me.email, scope }}
      account={
        <AccountMenu
          name={me.name}
          email={me.email}
          scope={scope}
          onOpenAccount={() => navigate({ to: '/account', search: (prev: Partial<ShellSearch>) => prev })}
          onOpenKeys={() => setKeysOpen(true)}
          onSignOut={endSession}
        />
      }
      title={active?.title}
      description={active?.description}
      breadcrumb={
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-md)', flexWrap: 'wrap' }}>
          {active && (
            <Breadcrumb
              items={[{ label: 'Console', onClick: () => navigate({ to: '/' }) }, { label: active.group }, { label: active.title }]}
            />
          )}
          <span style={{ flex: 1 }} />
          {/* The scope you hold, not a scope you choose. It was a switcher while the console had
              no authentication and every role had to be reviewable; now the server grants it, and
              a console shaped around a scope you do not hold is a set of doors that 403. */}
          <Kicker>Acting as</Kicker>
          <Chip variant="filter" label={scope} selected />
        </div>
      }
    >
      {/* A deploy freeze is said on every page, so nobody learns of it from a refused deploy (S09). */}
      <FreezeBanner />
      {scope === 'none' ? (
        <EmptyState
          icon="shield"
          title="Your account has no scopes yet"
          body="A new admin starts with none. Ask a superadmin to grant the scopes your role needs — nothing here is hidden from you by mistake."
        />
      ) : allowed ? (
        <>
          {/* Before the screen, not after it: a notice below the numbers is a notice read second. */}
          {active && <ScreenNotice wiring={active.wiring} />}
          <Outlet />
        </>
      ) : (
        <EmptyState
          icon="shield"
          title={`${active!.title} needs a scope you do not hold`}
          body={`This section belongs to ${active!.scopes.join(' or ')}. You hold ${scope}, which owns: ${SCOPE_OWNS[scope]}.`}
        />
      )}
      <IdleLock open={locked} reason={lockReason} name={me.name} email={me.email} onUnlock={() => setLocked(false)} onSignOut={endSession} />
      <CommandPalette open={paletteOpen} modules={reachable} onGo={go} onClose={() => setPaletteOpen(false)} />
      <KeyboardOverlay open={keysOpen} onClose={() => setKeysOpen(false)} />
    </AppShell>
  );
}
